/**
 * Canonical max *output* token limits per model.
 * Keep in sync with: backend/src/utils/modelOutputTokenLimits.js
 */

export const MODEL_MAX_OUTPUT_TOKENS = {
  'gpt-3.5-turbo': 4096,
  'gpt-4': 8192,
  'gpt-4-turbo': 4096,
  'gpt-4o': 16384,
  'gpt-4o-mini': 16384,
  'gpt-4.1': 32768,
  'gpt-4.1-mini': 32768,
  'gpt-4.1-nano': 32768,
  'gpt-5': 128000,
  'gpt-5.1': 128000,
  'gpt-5.6': 128000,
  'gpt-5-mini': 100000,
  'gpt-5-nano': 128000,

  'gemini-1.5-flash': 8192,
  'gemini-1.5-pro': 8192,
  'gemini-2.0-flash-lite': 8192,
  'gemini-2.0-flash': 8192,
  'gemini-2.0': 8192,
  'gemini-2.0-pro': 8192,
  'gemini-2.5-flash-lite': 65536,
  'gemini-2.5-flash': 65536,
  'gemini-2.5': 65536,
  'gemini-2.5-pro': 65536,
  'gemini-3-flash-preview': 65536,
  'gemini-3.1-flash-lite': 65536,
  'gemini-3.1-flash-lite-preview': 65536,
  'gemini-3.5-flash-lite': 65536,
  'gemini-3.5-flash': 65536,
  'gemini-3.6-flash': 65536,
  'gemini-3.7-flash': 65536,
  'gemini-3.1-pro-preview': 65536,

  'qwen-turbo': 8192,
  'qwen-plus': 32768,
  'qwen-max': 32768,
  'qwen3.8-flash': 131072,
  'qwen3.8-max': 131072,
  'qwen3.7-plus': 65536,
  'qwen3.7-flash': 65536,
  'qwen3-vl-plus': 32768,
  'qwen3-vl-flash': 32768,
  'qwen-vl-plus': 8192,
  'qwen-vl-max': 32768,
  'qwen-vl-max-latest': 32768,
  'qwen-vl-chat': 8192,

  'anthropic-claude-sonnet-4': 64000,
  'anthropic-claude-opus-4': 64000,
  'anthropic-claude-sonnet-4-5': 64000,
  'anthropic-claude-opus-4-5': 64000,
  'anthropic-claude-sonnet-4-6': 128000,
  'anthropic-claude-opus-4-8': 128000,
  'anthropic-claude-sonnet-5': 128000,
  'anthropic-claude-opus-5': 128000,
  'anthropic-claude-fable-5': 128000,

  'bedrock-claude-3-haiku': 4096,
  'bedrock-claude-3-sonnet': 4096,
  'bedrock-claude-3-5-sonnet': 8192,
  'bedrock-claude-3-5-sonnet-v2': 8192,
  'bedrock-claude-3-7-sonnet': 64000,
  'bedrock-claude-sonnet-4': 64000,
};

const DEFAULT_MAX_OUTPUT_TOKENS = 8192;

export function getMaxOutputTokens(model) {
  if (!model || typeof model !== 'string') {
    return DEFAULT_MAX_OUTPUT_TOKENS;
  }

  const key = model.trim();
  if (MODEL_MAX_OUTPUT_TOKENS[key] != null) {
    return MODEL_MAX_OUTPUT_TOKENS[key];
  }

  const lower = key.toLowerCase();

  if (lower.startsWith('gpt-5.6') || lower.startsWith('gpt-5.1') || lower === 'gpt-5') return 128000;
  if (lower.startsWith('gpt-5-mini')) return 100000;
  if (lower.startsWith('gpt-5-nano')) return 128000;
  if (lower.startsWith('gpt-4.1')) return 32768;
  if (lower.startsWith('gpt-4o')) return 16384;
  if (lower.startsWith('gpt-4-turbo')) return 4096;
  if (lower.startsWith('gpt-4')) return 8192;
  if (lower.startsWith('gpt-3.5')) return 4096;

  if (lower.startsWith('gemini-3') || lower.startsWith('gemini-2.5')) return 65536;
  if (lower.startsWith('gemini-2.0') || lower.startsWith('gemini-1.5') || lower.startsWith('gemini-')) return 8192;

  if (lower.startsWith('qwen3.8')) return 131072;
  if (lower.startsWith('qwen3.7') || lower.startsWith('qwen3.6') || lower.startsWith('qwen3.5')) return 65536;
  if (lower.includes('qwen3-vl')) return 32768;
  if (lower.includes('qwen-vl-max')) return 32768;
  if (lower.includes('qwen-vl')) return 8192;
  if (lower.includes('qwen-max') || lower.includes('qwen-plus')) return 32768;
  if (lower.includes('qwen-turbo')) return 8192;

  if (/claude-(fable-5|opus-5|sonnet-5|opus-4-8|sonnet-4-6)/.test(lower)) return 128000;
  if (/claude-(haiku-4-5|sonnet-4-5|opus-4-5|opus-4-1|sonnet-4)/.test(lower)) return 64000;
  if (lower.includes('claude')) return 64000;

  return DEFAULT_MAX_OUTPUT_TOKENS;
}

export function getActiveVisionModel(config = {}) {
  const method = config.visionMethod || 'responses';
  if (method === 'gemini') return config.visionGeminiModel || 'gemini-2.5-flash-lite';
  if (method === 'qwen') return config.visionQwenModel || 'qwen-vl-plus';
  if (method === 'enhanced') return config.visionEnhancedModel || 'gpt-4o';
  if (method === 'quick') return config.visionQuickModel || 'gpt-4o-mini';
  if (method === 'auto') return config.visionResponsesModel || 'gpt-4.1-mini';
  return config.visionResponsesModel || 'gpt-4.1-mini';
}

export function formatTokenCount(n) {
  if (n == null) return '';
  return Number(n).toLocaleString();
}
